import React, { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Mail } from "lucide-react";
import { useRole } from "../context/RoleContext";
import { getEmailDetail, formatTimestamp } from "../lib/notifications";
import BackBar from "../components/BackBar";

export default function AdminEmailDetail() {
  const { emailId } = useParams();
  const { user } = useRole();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!user?.id || !emailId) return;
    setData(null);
    setError(null);
    getEmailDetail(emailId, user.id)
      .then(setData)
      .catch((e) => setError(e?.response?.data?.detail || "Email not found"));
  }, [user?.id, emailId]);

  return (
    <section className="animate-fade-in-up" data-testid="admin-email-detail-page">
      <BackBar
        parent={{ to: "/admin/email-log", label: "Email log" }}
        trail={[
          { label: "Admin", to: "/admin" },
          { label: "Email log", to: "/admin/email-log" },
          { label: emailId },
        ]}
      />

      {error && (
        <div
          className="wn-card"
          style={{ padding: 24, borderColor: "#D9D8E4" }}
          data-testid="admin-email-detail-error"
        >
          <div style={{ fontWeight: 700, color: "#A23B2E", marginBottom: 6 }}>{error}</div>
          <button
            type="button"
            className="wn-btn wn-btn-tertiary"
            onClick={() => navigate("/admin/email-log")}
          >
            Back to the email log
          </button>
        </div>
      )}

      {!error && data === null && (
        <div style={{ color: "#6C6B85", fontStyle: "italic" }}>Loading email…</div>
      )}

      {!error && data && (
        <div className="wn-card" style={{ padding: 0, overflow: "hidden", maxWidth: 760 }}>
          <div
            style={{
              padding: "16px 22px",
              borderBottom: "1px solid #D9D8E4",
              background: "#F5F4FA",
            }}
          >
            <div className="wn-label" style={{ color: "#3A5563" }}>
              <Mail size={11} style={{ display: "inline", verticalAlign: "middle", marginRight: 5 }} />
              Simulated email · not sent
            </div>
            <h1
              className="mt-2"
              style={{
                fontFamily: '"Fraunces", Georgia, serif',
                fontSize: 22,
                fontWeight: 600,
                color: "#1B1F4B",
                lineHeight: 1.25,
              }}
              data-testid="admin-email-detail-subject"
            >
              {data.subject || "—"}
            </h1>
          </div>

          <dl
            style={{
              padding: "16px 22px",
              display: "grid",
              gridTemplateColumns: "auto 1fr",
              columnGap: 16,
              rowGap: 8,
              borderBottom: "1px solid #D9D8E4",
            }}
            data-testid="admin-email-detail-headers"
          >
            <Header k="From" v="notifications@wholeneighborhoods.org (simulated)" />
            <Header k="To" v={`${data.to_name || ""} <${data.to || "—"}>`} />
            <Header k="Sent" v={<span className="tabular-nums">{formatTimestamp(data.sent_at)}</span>} />
            <Header k="Event" v={prettyKind(data.kind)} />
            {data.related_record?.id && (
              <Header
                k="Record"
                v={
                  <span className="tabular-nums" style={{ color: "#3A5563" }}>
                    {data.related_record.type} / {data.related_record.id}
                  </span>
                }
              />
            )}
          </dl>

          <div
            style={{ padding: "22px 22px", color: "#272636", fontSize: 14, lineHeight: 1.65, whiteSpace: "pre-wrap" }}
            data-testid="admin-email-detail-body"
          >
            {data.body || "—"}
          </div>
        </div>
      )}
    </section>
  );
}

function Header({ k, v }) {
  return (
    <>
      <dt className="wn-label" style={{ color: "#6C6B85", paddingTop: 2 }}>{k}</dt>
      <dd style={{ color: "#272636", fontSize: 13.5 }}>{v}</dd>
    </>
  );
}

function prettyKind(k) {
  if (!k) return "—";
  return k.replace(/_/g, " ").replace(/\./g, " → ").replace(/\b\w/g, (c) => c.toUpperCase());
}
